import { string } from "prop-types";
import { useEffect, useState } from "react";

import styled from "styled-components";
import { themeSizes, themeVariants } from "theme";

import { AnimatePresence, motion } from "framer-motion";

import { slideDown, slideUp } from "UI/motionStyles";

import { iconWidths } from "./interpolations";

const IconWrapper = styled("div")`
  position: absolute;
  color: ${({ $iconColor, theme: { colors, palette } }) =>
    colors[$iconColor] || palette[$iconColor] || $iconColor};
  font-size: ${({ $size }) => {
    switch ($size) {
      case themeSizes.lg:
        return "24px";
      case themeSizes.xl:
        return "28px";
      default:
        return "21px"; // these are legacy sizes. We should standardize these with theme.fontSizes someday.
    }
  }};
  /* font-size: 21px; */
  width: ${({ $size }) => iconWidths[$size]}px;
  height: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: translate(1px, -50%);
  top: 50%;
`;

const Icon = styled(motion.span)`
  position: absolute;
  width: 25px;
  height: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const useAnimationProps = ({ icons, icon, activeIdx }) => {
  const [lastAnimationProps, setLastAnimationProps] = useState(slideUp);
  const oldIndex = icons.indexOf(icon);

  // if icons does not yet have the record, slide up.
  if (oldIndex === -1) {
    setLastAnimationProps(slideDown);
    return slideDown;
  }

  // if advancing, slide up.
  if (activeIdx > oldIndex) {
    setLastAnimationProps(slideUp);
    return slideUp;
  }

  // if regressing, slide down.
  if (oldIndex > activeIdx) {
    setLastAnimationProps(slideDown);
    return slideDown;
  }

  // if equal, return the same as last time.
  return lastAnimationProps;
};

const AnimatedIcon = ({ icon, iconColor, size }) => {
  const [icons, setIcons] = useState([icon]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [iconAnimationKey, setIconAnimationKey] = useState(icon);
  const [activeKey, setActiveKey] = useState(icon);

  useEffect(() => {
    setActiveKey(icon);
  }, [icon]);

  const animationProps = useAnimationProps({
    icons,
    icon,
    activeIdx,
  });

  if (iconAnimationKey !== icon) {
    setIconAnimationKey(icon);
  }
  useEffect(() => {
    setIconAnimationKey(icon);
  }, [icon]);

  // get active index ready for next transition
  if (!icons.includes(icon)) {
    setActiveIdx(icons.length);
    setIcons([...icons, icon]);
  } else if (icons.indexOf(icon) !== activeIdx) {
    setActiveIdx(icons.indexOf(icon));
  }
  if (!icon) return null;
  return (
    <IconWrapper
      $variant={themeVariants.icon}
      $iconColor={iconColor}
      $size={size}
      data-cy="FnrInput_Icon"
    >
      <AnimatePresence initial={false}>
        {icons.map((elem) => {
          if (elem === activeKey)
            return <Icon {...animationProps} key={elem} className={elem} />;
          return null;
        })}
      </AnimatePresence>
    </IconWrapper>
  );
};

AnimatedIcon.propTypes = { icon: string, iconColor: string, size: string };

export default AnimatedIcon;
